import { useContext, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import FileUpload from '#components/FileUpload';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import {
  DragDropContext, Droppable, Draggable, DropResult,
} from '@hello-pangea/dnd';

import { productValidations } from '#common/validation';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import { KEY_CODES } from '#utils/constants';
import ChipInput from '#components/ChipInput';
import EnhancedAutocomplete from '#components/EnhancedAutocomplete';
import EnhancedTable, { EnhancedTableRow } from '#components/EnhancedTable';
import { useEnhancedTable } from '#components/EnhancedTable.utils';
import TitleBar, { TitleBarItemTraveler } from '#components/TitleBar';
import useForm from '#utils/use-form';
import { resizeImageFile } from '#utils/resizeImage';
import {
  useSaveProductMutation,
} from '#services/product';
import {
  useData,
  composeVariants,
  getInitialState,
  duplicateItem,
  ProductFormState,
  ProductVariant,
} from './Edit.utils';
import VideoList from './Edit.VideoList';
import Customization from './Edit.Customization';
import ShippingWindow from './Edit.ShippingWindow';

type MediaItem = string | { _pendingId: string; path?: string; [key: string]: unknown };

interface InventoryProductItem {
  size: string;
  color: string;
  amounts?: number;
  [key: string]: unknown;
}

interface VendorOption {
  name: string;
  [key: string]: unknown;
}

type TableError = { rowIndex: number; [key: string]: unknown };
type BatchListError = { rowIndex: number; [key: string]: unknown };
type BatchRow = Record<string, string>;

function restoreLockedValues(originalValue: string[], newValue: string[], lock: string[]) {
  // assume orignalValue and newValue is array of strings

  const missing = lock.filter((n) => !newValue.includes(n));

  missing.forEach((mis) => {
    const str = originalValue.join(',');
    const [prev, next] = str.split(`${mis},`)
      .map((p) => p.split(',').filter((n) => n)) as [string[], string[]];

    // insert from prev list
    let inserted = false;
    prev.reverse().some((p) => {
      const index = newValue.indexOf(p);
      if (index === -1) {
        return false;
      }
      newValue.splice(index + 1, 0, mis);
      inserted = true;
      return true;
    });

    if (inserted) {
      return;
    }

    next.some((p) => {
      const index = newValue.indexOf(p);
      if (index === -1) {
        return false;
      }
      newValue.splice(index, 0, mis);
      return true;
    });
  });

  return newValue;
}

export default function ProductEdit() {
  const appConfig = useSelector((state: { appConfig: { shippingWindowInWeeks: string } }) => state.appConfig);
  const {
    showBackdrop,
    hideBackdrop,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const shippingWindowInWeeks = parseInt(appConfig.shippingWindowInWeeks, 10);
  const {
    values,
    setValues,
    isValid,
    isDirty,
    setDirty,
    errors,
    errorMessages,
    handleChange: rawHandleChange,
    handleSubmit,
    addSubmitListener,
    resetForm,
  } = useForm({
    initialState: getInitialState(null, { shippingWindowInWeeks }),
    validations: productValidations as Parameters<typeof useForm>[0]['validations'],
  });

  // useForm's handleChange accepts a narrow event/change shape; the many call
  // sites here pass MUI/synthetic events and ad-hoc change descriptors, so relax
  // it to a permissive callback (still assignable to every onChange prop below).
  const handleChange = rawHandleChange as (event?: unknown, data?: unknown) => void;

  const tableProps = useEnhancedTable({});

  const location = useLocation();
  const navigate = useNavigate();

  const [resetFlag, setResetFlag] = useState(0);
  const pendingCounterRef = useRef(0);
  const [saveProduct] = useSaveProductMutation();

  const urlParam = new URLSearchParams(location.search);
  const isEditMode = urlParam.has('productIndex') || urlParam.has('productId');
  const isDuplicateMode = urlParam.has('duplicate');
  const productIndex = Number(urlParam.get('productIndex'));

  useData({
    resetForm,
    isDuplicateMode,
    isEditMode,
    shippingWindowInWeeks,
  });

  const handleTravelProduct = (position: number) => {
    urlParam.set('productIndex', String(productIndex + position));
    const search = urlParam.toString();
    void navigate({
      pathname: '/management/product/edit',
      search,
    });
  };

  const getMediaKey = (item: MediaItem): string => (
    typeof item === 'string' ? item : item._pendingId
  );

  const handleRemoveMedia = (itemKey: string) => {
    const newMedia = (values.media as MediaItem[]).filter((m) => getMediaKey(m) !== itemKey);
    setDirty(true);
    setValues({ media: newMedia });
  };

  const handleReorderMedia = (result: DropResult) => {
    // Dropped outside the list
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    // If the item is dropped in the same position, do nothing
    if (sourceIndex === destinationIndex) {
      return;
    }

    // Create a copy of the media array
    const newMedia = [...(values.media as MediaItem[])];

    // Remove the item from its original position
    const movedItem = newMedia.splice(sourceIndex, 1)[0]!;

    // Insert the item at the new position
    newMedia.splice(destinationIndex, 0, movedItem);

    // Update the state
    setDirty(true);
    setValues({ media: newMedia });
  };

  const handleSaveProduct = (formValues: ProductFormState, formIsValid: boolean) => {
    if (!formIsValid) {
      return;
    }

    const postData: Record<string, unknown> = { ...formValues };
    delete postData.availableCollections;
    delete postData.availableVendors;
    delete postData.showVideoFirst;

    // Separate existing filenames from pending uploads in the gallery
    const mediaItems = formValues.media as MediaItem[];
    const existingMedia = mediaItems.filter((m): m is string => typeof m === 'string');
    const pendingFiles = mediaItems.filter(
      (m): m is Exclude<MediaItem, string> => typeof m === 'object',
    );
    postData.media = existingMedia;
    postData.files = pendingFiles;
    if (pendingFiles.length > 0) {
      // Map each pending item to a placeholder index so the server
      // can interleave new filenames into the correct positions
      const pendingPlaceholders = new Map(
        pendingFiles.map((f, i) => [f._pendingId, `__pending_${i}`]),
      );
      postData.finalOrder = mediaItems.map((m) => (
        typeof m === 'string' ? m : pendingPlaceholders.get(m._pendingId)
      ));
    }

    showBackdrop();

    void saveProduct(postData)
      .then((res) => {
        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], `Product "${formValues.name as string}" was successfully saved.`);
        if (hasError) {
          return;
        }

        if (isEditMode) {
          // edit mode
          void navigate(location.pathname.replace('/edit', '/list'));
        } else {
          // add new mode
          resetForm({
            ...getInitialState(null, { shippingWindowInWeeks }),
            availableCollections: formValues.availableCollections,
            availableVendors: formValues.availableVendors,
          });
          setResetFlag(resetFlag + 1);
        }
      }).finally(() => {
        hideBackdrop();
      });
  };
  addSubmitListener(handleSaveProduct);

  const handleDuplicateItem = () => {
    setValues(duplicateItem(values));
    void navigate(`${location.pathname}?duplicate=${values.id as string | number}`);
  };

  return (
    <>
      <TitleBar
        title={`${isEditMode ? 'Edit' : 'Add'} Product`}
        hideActionButtons={!productIndex}
      >
        <Button
          variant="outlined"
          onClick={handleSubmit}
          sx={{ mr: 1 }}
          disabled={!isDuplicateMode && (!isValid || !isDirty)}
        >
          Save
        </Button>
        <Button
          variant="outlined"
          disabled={!isEditMode}
          onClick={handleDuplicateItem}
        >
          Duplicate
        </Button>
        <TitleBarItemTraveler
          disabled={isDuplicateMode || !isEditMode}
          onTravelItem={(direct) => handleTravelProduct(direct)}
          itemIndex={values.productIndex as number}
          itemSize={values.total as number}
        />
      </TitleBar>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Box />
        <FormControlLabel
          sx={{
            mx: 1,
          }}
          control={(
            <Switch
              checked={!values.inactivated}
              onChange={(e) => {
                e.target.checked = !e.target.checked;
                handleChange(e);
              }}
              name="inactivated"
              color="primary"
            />
          )}
          label="Activated"
        />
      </Box>

      <Box sx={{ pb: 1 }}>
        <TextField
          required
          fullWidth
          size="small"
          name="name"
          label="Name"
          value={values.name}
          error={!!errors.name}
          onChange={handleChange}
        />
        <TextField
          required
          fullWidth
          multiline
          rows={4}
          size="small"
          name="description"
          label="Description"
          value={values.description}
          error={!!errors.description}
          onChange={handleChange}
        />
      </Box>

      <Box sx={{ pt: 2, pb: 1 }}>
        <Typography variant="subtitle2">
          Photos
        </Typography>
        <DragDropContext onDragEnd={handleReorderMedia}>
          <Droppable droppableId="media-list" direction="horizontal">
            {(provided) => (
              <Box
                sx={{
                  overflowX: 'scroll',
                  height: '130px',
                  whiteSpace: 'nowrap',
                }}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {(values.media as MediaItem[]).map((item, index) => {
                  const itemKey = getMediaKey(item);
                  const imgSrc = typeof item === 'string'
                    ? `/images/product_${(values.id as number).toString().padStart(8, '0')}/${item}`
                    : item.path;
                  return (
                    <Draggable key={itemKey} draggableId={itemKey} index={index}>
                      {(provided, snapshot) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{
                            display: 'inline-block',
                            position: 'relative',
                            margin: '0 4px',
                            border: snapshot.isDragging ? '2px dashed #1976d2' : '1px solid black',
                            backgroundColor: snapshot.isDragging ? 'rgba(25, 118, 210, 0.1)' : 'lightgrey',
                          }}
                        >
                          <Box
                            {...provided.dragHandleProps}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              color: 'white',
                              borderRadius: '0 0 4px 0',
                              cursor: 'grab',
                            }}
                          >
                            <DragIndicatorIcon fontSize="small" />
                          </Box>
                          <img
                            src={imgSrc}
                            alt=""
                            loading="lazy"
                            style={{
                              objectFit: 'contain',
                              maxHeight: '130px',
                              minHeight: '130px',
                              minWidth: '90px',
                            }}
                          />
                          <IconButton
                            sx={{
                              color: 'white',
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            }}
                            onClick={() => handleRemoveMedia(itemKey)}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>

        <Box id="upload-control" sx={{ mb: 2 }}>
          <FileUpload
            key={resetFlag}
            multiFile
            maxUploadFiles={0}
            maxFileSize={100}
            acceptedType="image/*"
            allowedExtensions={['jpg', 'jpeg', 'png']}
            bannerProps={{ elevation: 0, variant: 'outlined' }}
            containerProps={{ elevation: 0, variant: 'outlined' }}
            onFilesChange={async (files) => {
              setDirty(true);
              setResetFlag((prev) => prev + 1);
              const resized = await Promise.all(
                files.map((f) => resizeImageFile(f as unknown as Parameters<typeof resizeImageFile>[0])),
              );
              const pendingItems = resized.map((f) => ({
                _pendingId: `__pending_${(pendingCounterRef.current += 1)}`,
                ...f,
              }));
              setValues((prev) => ({ media: [...(prev.media as MediaItem[]), ...pendingItems] }));
            }}
          />
        </Box>
      </Box>

      <Box sx={{ pt: 2, pb: 1 }}>
        <Typography variant="subtitle2">
          Videos
          <FormControlLabel
            control={(
              <Checkbox
                name="showVideoFirst"
                checked={values.showVideoFirst as boolean}
                onChange={handleChange}
              />
              )}
            label="Show at the beginning of the list"
            sx={{
              color: '#1976d2', ml: 1,
            }}
          />
        </Typography>
        <Typography variant="overline" gutterBottom sx={{ fontStyle: 'italic' }}>
          short in vertical orientation preferred
        </Typography>
        <VideoList
          name="videos"
          sizes={[10]}
          value={values.videos as BatchRow[]}
          errors={errorMessages.videos as BatchListError[]}
          onChange={handleChange}
        />
      </Box>

      <Box sx={{ pt: 2, pb: 1 }}>
        <FormControl fullWidth>
          <InputLabel htmlFor="outlined-adornment-price">Price</InputLabel>
          <OutlinedInput
            required
            fullWidth
            size="small"
            type="number"
            name="price"
            label="Price"
            value={values.price}
            error={!!errors.price}
            onChange={handleChange}
            onKeyDown={(event) => {
              if (event.keyCode === KEY_CODES.ENTER) {
                const newVariants = (values.variants as ProductVariant[])
                  .map((v) => ({ ...v, price: Number(values.price) }));
                handleChange(null, {
                  name: 'variants',
                  value: newVariants,
                });
              }
            }}
            onBlur={() => {
              const newVariants = (values.variants as ProductVariant[])
                .map((v) => ({ ...v, price: Number(values.price) }));
              handleChange(null, {
                name: 'variants',
                value: newVariants,
              });
            }}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
          />
        </FormControl>
      </Box>

      <Box sx={{ pt: 2, pb: 1 }}>
        <Typography variant="subtitle2">
          Options
        </Typography>
        <Paper variant="outlined" sx={{ px: 2, pt: 2, pb: 1 }}>
          <ChipInput
            fullWidth
            required
            sx={{ mb: 1 }}
            name="sizes"
            label="Size"
            value={values.sizes as string[]}
            error={!!errors.sizes}
            getLocked={(tag) => (values.inventoryProduct as InventoryProductItem[]).some((ip) => ip.size === tag)}
            onChange={(event, data) => {
              // restore sizes exists in inventory product list
              const locked = (values.inventoryProduct as InventoryProductItem[])
                .filter((ip) => ip.amounts)
                .map((ip) => ip.size)
                // uniq the locked value
                .filter((elem, index, self) => self.indexOf(elem) === index);

              const final = restoreLockedValues(
                values.sizes as string[],
                data.value,
                locked,
              );

              const newVariants = composeVariants(
                final,
                values.colors as string[],
                values.variants as ProductVariant[],
                Number(values.price),
              );
              handleChange(event, [
                data,
                {
                  name: 'variants',
                  value: newVariants,
                },
              ]);
            }}
          />
          <ChipInput
            fullWidth
            required
            sx={{ mb: 1 }}
            name="colors"
            label="Color"
            value={values.colors as string[]}
            error={!!errors.colors}
            getLocked={(tag) => (values.inventoryProduct as InventoryProductItem[]).some((ip) => ip.color === tag)}
            onChange={(event, data) => {
              // restore colors exists in inventory product list
              const locked = (values.inventoryProduct as InventoryProductItem[])
                .filter((ip) => ip.amounts)
                .map((ip) => ip.color)
                // uniq the locked value
                .filter((elem, index, self) => self.indexOf(elem) === index);

              const final = restoreLockedValues(
                values.colors as string[],
                data.value,
                locked,
              );

              const newVariants = composeVariants(
                values.sizes as string[],
                final,
                values.variants as ProductVariant[],
                Number(values.price),
              );
              handleChange(event, [
                data,
                {
                  name: 'variants',
                  value: newVariants,
                },
              ]);
            }}
          />
        </Paper>
      </Box>

      <Box sx={{ pt: 2, pb: 1 }}>
        <Typography variant="subtitle2">
          Variants
          <FormControlLabel
            control={(
              <Checkbox
                checked={(values.variants as ProductVariant[]).every((v) => v.inventoryOnly)}
                onChange={(e, selected) => {
                  (values.variants as ProductVariant[]).forEach((va) => {
                    va.inventoryOnly = selected;
                  });
                  handleChange(e, [{
                    name: 'variants',
                    value: values.variants,
                  }]);
                }}
              />
            )}
            label='Mark all as "Inventory Only"'
            sx={{
              color: '#1976d2', ml: 1,
            }}
          />
        </Typography>

        <EnhancedTable
          {...tableProps}
          name="variants"
          headers={[{
            id: 'variant',
            label: 'Variant',
          }, {
            id: 'price',
            label: 'Price',
            type: 'number',
            editable: true,
            onChange: (event, data) => {
              const v = (values.variants as ProductVariant[]).find((va) => va.index === data.rowIndex);
              if (v) {
                v.price = (event as { target: { value: string } }).target.value;
              }
              handleChange(event, {
                name: 'variants',
                value: values.variants,
              });
            },
          }, {
            id: '_inventory',
            label: 'Inventory',
            renderer: (r) => {
              const variant = (values.variants as ProductVariant[]).find((va) => (va.index === r.index));
              const inventory = (values.inventoryProduct as InventoryProductItem[]).find((ip) => variant?.variant === `${ip.size} / ${ip.color}`);
              if (!inventory) {
                return '-';
              }
              return inventory.amounts;
            },
          }, {
            id: '_inventoryOnly',
            label: 'Inventory Only',
            renderer: (r) => (
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={!!r.inventoryOnly}
                    onChange={(e, selected) => {
                      const v = (values.variants as ProductVariant[]).find((va) => va.index === r.index);
                      if (v) {
                        v.inventoryOnly = selected;
                      }
                      handleChange(e, {
                        name: 'variants',
                        value: values.variants,
                      });
                    }}
                  />
                )}
                label=""
              />
            ),
          }]}
          errors={errorMessages.variants as TableError[]}
          rows={values.variants as EnhancedTableRow[]}
        />
      </Box>

      <Box sx={{ pt: 2, pb: 1 }}>
        <Typography variant="subtitle2">
          Customization
        </Typography>
        <Customization
          name="customizations"
          value={values.customizations as BatchRow[]}
          sizes={[7, 3]}
          onChange={handleChange}
        />
      </Box>

      <Box sx={{ pt: 2, pb: 1 }}>
        <Typography variant="subtitle2">
          Shipping Window
        </Typography>
        <ShippingWindow
          name="shippingWindow"
          value={values.shippingWindow as BatchRow[]}
          sizes={[4, 3, 3]}
          onChange={handleChange}
        />
      </Box>

      <Box sx={{ pt: 2, pb: 1 }}>
        <Typography variant="subtitle2">
          Other Properties
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, pb: 1, mb: 1 }}>
          <Autocomplete
            fullWidth
            size="small"
            options={values.availableVendors as VendorOption[]}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            value={(values.availableVendors as VendorOption[]).find((vendor) => vendor.name === values.vendor) || null}
            onChange={(event, vendor) => handleChange(event, {
              name: 'vendor',
              value: vendor?.name || '',
            })}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                fullWidth
                name="vendor"
                label="Vendor"
                error={!!errors.vendor}
                id="Vendor"
              />
            )}
          />
          <EnhancedAutocomplete
            multiple
            disableCloseOnSelect
            name="collections"
            size="small"
            options={values.availableCollections as { id: number; name: string }[]}
              // props for enhanced
            label="Collections"
            error={!!errors.collectionIds}
            minSize={1}
            selectedIdsName="collectionIds"
            selectedIds={values.collectionIds as number[]}
            newValuesName="newCollections"
            newValues={values.newCollections as string[]}
            onChange={handleChange}
          />
          <ChipInput
            fullWidth
            sx={{ mb: 1 }}
            name="tags"
            value={values.tags as string[]}
            error={!!errors.tags}
            label="Tags"
            onChange={handleChange}
          />
        </Paper>
      </Box>
      <Button
        variant="outlined"
        onClick={handleSubmit}
        sx={{ mr: 1 }}
        disabled={!isDuplicateMode && (!isValid || !isDirty)}
      >
        Save
      </Button>
    </>
  );
}
